import React, { useMemo, useState } from "react";
import { Box, Typography, Stack, alpha, LinearProgress } from "@mui/material";
import {
  KeyboardArrowUp,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  SkipNext,
  PeopleAlt,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { selectAllUsers } from "../../../../../features/userSlice";
import { setSelectedAssigneeTasks } from "../../../../../features/dataAccuracySlice";

interface TaskStatus {
  toDo: number;
  inProgress: number;
  review: number;
  snoozed: number;
  done: number;
}

interface Assignee {
  id: string;
  name: string;
  taskCount: number;
  percentage: number;
  status: TaskStatus;
}

const statusConfig = {
  toDo: { label: "To Do", color: "#A24EBA" },
  inProgress: { label: "In Progress", color: "#F06292" },
  review: { label: "Review", color: "#F50057" },
  snoozed: { label: "Snoozed", color: "#FF9800" },
  done: { label: "Done", color: "#3384E8" },
};

const ITEMS_PER_PAGE = 5;

export const TopAssigneesView: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const handleAssigneeClick = (assigneeName: string) => {
    const isAlreadySelected =
      selectedAssigneeTasks &&
      selectedAssigneeTasks.length === 1 &&
      selectedAssigneeTasks[0] === assigneeName;
    dispatch(setSelectedAssigneeTasks(isAlreadySelected ? [] : [assigneeName]));
  };

  // Read the same Redux state as DataAccuracyTable
  const {
    selectedDataAccuracy,
    selectedPriority,
    selectedCategory,
    selectedOwner,
    lastUpdatedDate,
    dataAccruracyData,
    selectedAssigneeTasks,
  } = useSelector((state: any) => state.dataAccuracy);

  const allUsers = useSelector(selectAllUsers);
  const hideDone = searchParams.get("doneIssues") === "hideDone";

  // Aggregate data by assignee from raw queryOutputs
  // Uses item.assignee (singular) to match the filter's assignee source
  const assigneeData: Assignee[] = useMemo(() => {
    const rawData: any[] = dataAccruracyData?.query_output || [];
    const assigneeMap = new Map<
      number,
      { name: string; status: TaskStatus; taskCount: number }
    >();

    for (const item of rawData) {
      if (!item.title || !item.task_structure_shared_uid) continue;

      const assigneeId = item.assignee;
      if (!assigneeId) continue;

      const user = allUsers.find(
        (u: any) => String(u.id) === String(assigneeId),
      );
      if (!user) continue;
      const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();

      // If assignee filter is active, only include selected assignees
      if (selectedAssigneeTasks && selectedAssigneeTasks.length !== 0) {
        if (!selectedAssigneeTasks.includes(name)) continue;
      }

      // Apply the same row-level filters as the table
      const openIssuesCount =
        (item.in_progress || 0) + (item.to_do || 0) + (item.in_review || 0);
      if (hideDone && openIssuesCount === 0) continue;
      // if (hideDone && item?.snooze > 0) continue;

      if (
        selectedDataAccuracy &&
        selectedDataAccuracy.length !== 0 &&
        !selectedDataAccuracy.includes(item?.title)
      )
        continue;

      if (
        selectedPriority &&
        selectedPriority.length !== 0 &&
        !selectedPriority.includes(item?.priority)
      )
        continue;

      if (
        selectedCategory &&
        selectedCategory.length !== 0 &&
        !selectedCategory.includes(item?.category)
      )
        continue;

      // Owner check: resolve owner id to name
      if (selectedOwner && selectedOwner.length !== 0) {
        const ownerId = item?.owner;
        const ownerUser = allUsers.find(
          (u: any) => String(u.id) === String(ownerId),
        );
        const ownerName = ownerUser
          ? `${ownerUser.first_name || ""} ${ownerUser.last_name || ""}`.trim()
          : "";
        if (!ownerName || !selectedOwner.includes(ownerName)) continue;
      }

      if (
        lastUpdatedDate &&
        lastUpdatedDate.length === 2 &&
        lastUpdatedDate[0] &&
        lastUpdatedDate[1]
      ) {
        const itemDate = new Date(item?.last_updated);
        const startDate = new Date(lastUpdatedDate[0]);
        const endDate = new Date(
          new Date(lastUpdatedDate[1]).setHours(23, 59, 59, 999),
        );

        if (lastUpdatedDate[0] === lastUpdatedDate[1]) {
          if (
            new Date(item?.created_at).toDateString() !==
            startDate.toDateString()
          ) {
            continue;
          }
        } else if (itemDate < startDate || itemDate > endDate) {
          continue;
        }
      }

      const toDo = Number(item.to_do) || 0;
      const inProgress = Number(item.in_progress) || 0;
      const review = Number(item.in_review) || 0;
      const snoozed = Number(item.snooze) || 0;
      const done = Number(item.done) || 0;
      const rowTaskCount = toDo + inProgress + review;

      if (!assigneeMap.has(assigneeId)) {
        assigneeMap.set(assigneeId, {
          name,
          taskCount: 0,
          status: { toDo: 0, inProgress: 0, review: 0, snoozed: 0, done: 0 },
        });
      }

      const existing = assigneeMap.get(assigneeId)!;
      existing.taskCount += rowTaskCount;
      existing.status.toDo += toDo;
      existing.status.inProgress += inProgress;
      existing.status.review += review;
      existing.status.snoozed += snoozed;
      existing.status.done += done;
    }

    const allAssignees = Array.from(assigneeMap.entries());
    const totalTasks = allAssignees.reduce(
      (sum, [, data]) => sum + data.taskCount,
      0,
    );

    return allAssignees
      .map(([id, data]) => ({
        id: String(id),
        name: data.name,
        taskCount: data.taskCount,
        percentage:
          totalTasks > 0
            ? Math.round((data.taskCount / totalTasks) * 1000) / 10
            : 0,
        status: data.status,
      }))
      .sort((a, b) => b.taskCount - a.taskCount);
  }, [
    dataAccruracyData,
    allUsers,
    selectedAssigneeTasks,
    hideDone,
    selectedDataAccuracy,
    selectedPriority,
    selectedCategory,
    selectedOwner,
    lastUpdatedDate,
  ]);

  // Pagination
  const totalPages = Math.ceil(assigneeData.length / ITEMS_PER_PAGE);
  const paginatedData = assigneeData.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE,
  );

  const totalTasks = assigneeData.reduce(
    (sum, assignee) => sum + assignee.taskCount,
    0,
  );
  const totalAssignees = assigneeData.length;

  const renderStatusBar = (status: TaskStatus) => {
    const total =
      status.toDo +
      status.inProgress +
      status.review +
      status.snoozed +
      status.done;

    if (total === 0) return null;

    const segments = [
      {
        key: "toDo",
        value: (status.toDo / total) * 100,
        color: statusConfig.toDo.color,
      },
      {
        key: "inProgress",
        value: (status.inProgress / total) * 100,
        color: statusConfig.inProgress.color,
      },
      {
        key: "review",
        value: (status.review / total) * 100,
        color: statusConfig.review.color,
      },
      ...(!hideDone
        ? [
            {
              key: "snoozed",
              value: (status.snoozed / total) * 100,
              color: statusConfig.snoozed.color,
            },
            {
              key: "done",
              value: (status.done / total) * 100,
              color: statusConfig.done.color,
            },
          ]
        : []),
    ];

    let accumulatedWidth = 0;

    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: 16,
          borderRadius: "4px",
          overflow: "hidden",
          backgroundColor: alpha("#000", 0.04),
        }}
      >
        {segments.map((segment) => {
          const leftPosition = accumulatedWidth;
          accumulatedWidth += segment.value;

          if (segment.value === 0) return null;

          return (
            <Box
              key={segment.key}
              sx={{
                position: "absolute",
                left: `${leftPosition}%`,
                width: `${segment.value}%`,
              }}
            >
              <LinearProgress
                variant="determinate"
                value={100}
                sx={{
                  // height: "100%",
                  height: 16, // 👈 increase this (try 6, 8, 10, 12)
                  backgroundColor: "transparent",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: segment.color,
                    transform: "translateX(0) !important",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  },
                  "&:hover .MuiLinearProgress-bar": {
                    filter: "brightness(1.1)",
                  },
                }}
              />
            </Box>
          );
        })}
      </Box>
    );
  };

  // Summary bar for collapsed view
  // const renderSummaryBar = () => {
  //   return <Box sx={{ px: 3, pb: 1.5 }}>{renderStatusBar(overallStatus)}</Box>;
  // };

  if (assigneeData.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{ backgroundColor: "#FAFAFA", borderRadius: 1, padding: 1, mt: 1 }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1,
          mb: 0.5,
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <PeopleAlt
            sx={{
              color: "#7B1FA2",
              fontSize: 22,
              position: "relative",
              top: 12,
            }}
          />
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 500,
              color: "text.primary",
            }}
          >
            Quick View: Top Assignees
          </Typography>
        </Box>

        <Typography
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            fontSize: "13px",
            color: "#7B1FA2",
            fontWeight: 500,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {isExpanded && (
            <Typography
              onClick={() => setIsExpanded(false)}
              sx={{
                fontSize: "13px",
                color: "#7B1FA2",
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              HIDE
              <KeyboardArrowUp sx={{ fontSize: 16 }} />
            </Typography>
          )}
        </Typography>
      </Box>

      <Box
        sx={{
          borderColor: alpha("#000", 0.08),
        }}
      >
        {/* Collapsed View */}
        {!isExpanded && (
          <>
            <Box
              sx={{
                px: 1.5,
                pb: 1,
                ml: 4,
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                fontSize: "13px",
                color: "text.secondary",
              }}
            >
              {assigneeData.slice(0, 5).map((assignee) => {
                return (
                  <React.Fragment key={assignee.id}>
                    <Box
                      component="span"
                      onClick={() => handleAssigneeClick(assignee.name)}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        cursor: "pointer",
                        px: 0.75,
                        py: 0.25,
                      }}
                      data-tracker="642"
                    >
                      <Box component="span" sx={{ fontWeight: 500 }}>
                        {assignee.name}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 700,
                          color: "text.primary",
                        }}
                      >
                        {assignee.taskCount} ({assignee.percentage}%)
                      </Box>
                    </Box>
                  </React.Fragment>
                );
              })}
              <Typography
                onClick={() => setIsExpanded(true)}
                sx={{
                  fontSize: "13px",
                  color: "#7B1FA2",
                  fontWeight: 500,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
                data-tracker="641"
              >
                VIEW ALL
                <KeyboardArrowRight sx={{ fontSize: 16 }} />
              </Typography>
            </Box>
            {/* {renderSummaryBar()} */}
          </>
        )}

        {/* Expanded View */}
        {isExpanded && (
          <Box sx={{ px: 1, pb: 0, width: "50%" }}>
            <Typography
              sx={{
                fontSize: "12px",
                color: "text.primary",
                fontWeight: 500,
                mb: 2,
                ml: "34px",
              }}
            >
              {totalTasks} total tasks across {totalAssignees} assignees
            </Typography>

            <Stack spacing={1.5} sx={{ marginLeft: "34px" }}>
              {paginatedData.map((assignee, index) => (
                <Box
                  key={assignee.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                  data-tracker="642"
                >
                  {/* Rank */}
                  <Typography
                    sx={{
                      minWidth: 16,
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "text.primary",
                    }}
                  >
                    {currentPage * ITEMS_PER_PAGE + index + 1}
                  </Typography>

                  {/* Name */}
                  {(() => {
                    return (
                      <Typography
                        onClick={() => handleAssigneeClick(assignee.name)}
                        sx={{
                          minWidth: 140,
                          fontSize: "13px",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          cursor: "pointer",
                          borderRadius: "4px",
                          px: 0.5,
                        }}
                      >
                        {assignee.name}
                      </Typography>
                    );
                  })()}

                  {/* Status Bar */}
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {renderStatusBar(assignee.status)}
                  </Box>

                  {/* Task Count & Percentage */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      minWidth: 80,
                      justifyContent: "flex-start",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "text.primary",
                      }}
                    >
                      {assignee.taskCount}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "text.primary",
                      }}
                    >
                      ({assignee.percentage}%)
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>

            {/* Legend */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                pt: 2,
                // pb: 2,
                position: "relative",
                left: "50%",
                transform: "translateX(0%)",
              }}
            >
              {Object.entries(statusConfig).map(([key, config]) => {
                if (hideDone && (key === "done" || key === "snoozed"))
                  return null;
                return (
                  <Box
                    key={key}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "2px",
                        backgroundColor: config.color,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 500,
                        color: "text.secondary",
                      }}
                    >
                      {config.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  pt: 2,
                  position: "relative",
                  left: "50%",
                  transform: "translateX(0%)",
                }}
              >
                <Box
                  component="button"
                  onClick={() => setCurrentPage(0)}
                  disabled={currentPage === 0}
                  sx={{
                    border: "none",
                    background: "none",
                    cursor: currentPage === 0 ? "default" : "pointer",
                    opacity: currentPage === 0 ? 0.3 : 1,
                    display: "flex",
                    alignItems: "center",
                    p: 0.25,
                    color: "text.secondary",
                  }}
                >
                  <SkipNext
                    sx={{
                      transform: "rotate(180deg)",
                      fontSize: 26,
                      color: "secondary.dark",
                    }}
                  />
                </Box>
                <Box
                  component="button"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(0, prev - 1))
                  }
                  disabled={currentPage === 0}
                  sx={{
                    border: "none",
                    background: "none",
                    cursor: currentPage === 0 ? "default" : "pointer",
                    opacity: currentPage === 0 ? 0.3 : 1,
                    display: "flex",
                    alignItems: "center",
                    p: 0.25,
                    color: "text.secondary",
                  }}
                >
                  <KeyboardArrowLeft
                    sx={{ fontSize: 26, color: "secondary.dark" }}
                  />
                </Box>
                <Box
                  component="button"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))
                  }
                  disabled={currentPage >= totalPages - 1}
                  sx={{
                    border: "none",
                    background: "none",
                    cursor:
                      currentPage >= totalPages - 1 ? "default" : "pointer",
                    opacity: currentPage >= totalPages - 1 ? 0.3 : 1,
                    display: "flex",
                    alignItems: "center",
                    p: 0.25,
                    color: "text.secondary",
                  }}
                >
                  <KeyboardArrowRight
                    sx={{ fontSize: 26, color: "secondary.dark" }}
                  />
                </Box>
                <Box
                  component="button"
                  onClick={() => setCurrentPage(totalPages - 1)}
                  disabled={currentPage >= totalPages - 1}
                  sx={{
                    border: "none",
                    background: "none",
                    cursor:
                      currentPage >= totalPages - 1 ? "default" : "pointer",
                    opacity: currentPage >= totalPages - 1 ? 0.3 : 1,
                    display: "flex",
                    alignItems: "center",
                    p: 0.25,
                    color: "text.secondary",
                  }}
                >
                  <SkipNext sx={{ fontSize: 26, color: "secondary.dark" }} />
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TopAssigneesView;
